import React, { useEffect, useState } from "react";
import { MainLayout } from "../layout/mainLayout";
import TextField from "@mui/material/TextField";
import Grid2 from "@mui/material/Unstable_Grid2";
import {
    Autocomplete,
    AutocompleteRenderInputParams,
    Button,
} from "@mui/material";
import { Translations } from "../../models/translations";
import {
    getEmployeeGroupItems,
    getFunctionSettings,
    saveFunctionSettings,
} from "../../services/employeeGroupService";
import { showApiError } from "../framework/formUtils";
import { EmployeeGroupItem } from "../../models/employeeGroup/employeeGroupItem";
import { TESCalculationParameters } from "../../models/calculation/TESCalculationParameters";
import * as store from "../../framework/customStore";
import * as storeActions from "../../models/store/storeActions";
import i18n from "i18next";
import * as CustomStore from "../../framework/customStore";
import * as StoreActions from "../../models/store/storeActions";
import { CalcOvertimes } from "./CalcOvertimes";
import { LunchBreak } from "./LunchBreak";
import { Rounding } from "./Rounding";
import { CalcDailyAllowance } from "./CalcDailyAllowance";
import { LoadingIndicator } from "../framework/loadingIndicatorNew";
import { CalcFillingHours } from "./CalcFillingHours";
import { OwnerParameters } from "../../models/owner/ownerParameters";
import { CalcDailyAdditions } from "./CalcDailyAdditions";
import { CalcOtherAdditions } from "./CalcOtherAdditions";
import { CalcShiftSettings } from "./CalcShiftSettings";

type optionTp = { label: string; id: string };

export interface CalcGeneralProps {
    translate?: (str: string) => string;
    handleChange?: (attr: string, value: any) => void;
    settings?: TESCalculationParameters;
    setSettings?: (
        value: React.SetStateAction<TESCalculationParameters>
    ) => void;
}

export const CalculationMain = () => {
    const [settings, setSettings] = useState<TESCalculationParameters>();
    const [employeeGroups, setEmployeeGroups] = useState<optionTp[]>([]);
    const [employeeGroup, setEmployeeGroup] = useState<optionTp>({
        label: "",
        id: "",
    });
    const [saveButtonDisabled, setSaveButtonDisabled] = useState(false);
    const [searchIsOn, setSearchIsOn] = useState(true);
    const [started, setStarted] = useState(false);

    const ownerParameters = new OwnerParameters();

    const {
        overtimePeriodBoundaries: overtimeBoundaries,
        overtimeWeekBoundaries,
        overtimeDailyBoundaries,
        overtimeSaturdayBoundaries,
        dailyAllowanceBoundaries,
        isMonthlyOvertimeCalculationOn,
        isSaturdayCalculationOn,
        saturdayStartTime,
        saturdayEndTime,
    } = settings || {};

    const handleChange = (attr: string, value) => {
        setSettings({ ...settings, [attr]: value });
    };

    useEffect(() => {
        if (!started) {
            getEmployeeGroupItems(9999, 1, "", "Name", true)
                .then((response) => {
                    fillEmployeeGroups(response.items);
                    setStarted(true);
                })
                .catch(showApiError);
        }
    }, []);

    useEffect(() => {
        onEmployeeChanged(employeeGroup);
    }, [employeeGroup]);

    const fillEmployeeGroups = (items: EmployeeGroupItem[]) => {
        const groups = items.map((x) => ({ label: x.name, id: x.id }));
        setEmployeeGroups(groups);
        setEmployeeGroup(groups[0]);
    };

    const onEmployeeChanged = (value: optionTp) => {
        setSaveButtonDisabled(true);
        setSearchIsOn(true);

        if (value?.id) {
            getFunctionSettings(value.id)
                .then((response) => {
                    setSettings(response);
                })
                .catch(showApiError)
                .finally(() => {
                    setSaveButtonDisabled(false);
                    setSearchIsOn(false);
                });
        }
    };

    const validate = (): boolean => {
        if (
            [
                overtimeBoundaries,
                overtimeWeekBoundaries,
                overtimeDailyBoundaries,
                overtimeSaturdayBoundaries,
                dailyAllowanceBoundaries,
            ].some(
                (b) =>
                    b.length === 2 &&
                    b.find((v) => v.boundary === 50)?.boundaryValue >
                        b.find((v) => v.boundary === 100)?.boundaryValue
            )
        ) {
            CustomStore.customStore.dispatch(
                StoreActions.showErrorMessage(translate("CHECKBOUNDARYVALUE"))
            );
            return false;
        }

        if (isMonthlyOvertimeCalculationOn && overtimeBoundaries.length > 0) {
            CustomStore.customStore.dispatch(
                StoreActions.showErrorMessage(
                    translate("CheckMonthlyAndPeriodOvertime")
                )
            );
            return false;
        }

        if (
            isSaturdayCalculationOn &&
            saturdayEndTime.substring(0, 5) !== "00:00" &&
            saturdayEndTime <= saturdayStartTime
        ) {
            CustomStore.customStore.dispatch(
                StoreActions.showErrorMessage(
                    translate("saturdayEndTimeMustBeAfterStartTime")
                )
            );
            return false;
        }

        return true;
    };

    const saveParameters = () => {
        if (validate()) {
            if (employeeGroup) {
                setSaveButtonDisabled(true);
                setSearchIsOn(true);

                saveFunctionSettings(employeeGroup.id, settings)
                    .then(() => {
                        store.customStore.dispatch(
                            storeActions.showSuccessMessage(
                                Translations.SaveSuccess
                            )
                        );
                    })
                    .catch(() => {
                        store.customStore.dispatch(
                            storeActions.showErrorMessage(
                                translate("unexpectedError")
                            )
                        );
                    })
                    .finally(() => {
                        setSaveButtonDisabled(false);
                        setSearchIsOn(false);
                    });
            }
        }
    };

    const translate = (str: string): string => {
        //i18n using not well done interface so.
        /*eslint-disable   @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call */
        return i18n.t(str);
    };

    return (
        <MainLayout
            topComponent={
                <Grid2
                    container
                    direction="row"
                    paddingLeft={2}
                    alignItems={"center"}
                >
                    <Autocomplete
                        className="field-width-normal"
                        isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                        }
                        renderInput={(
                            params: AutocompleteRenderInputParams
                        ) => (
                            <TextField
                                {...params}
                                label={Translations.EmployeeGroup}
                            />
                        )}
                        onChange={(event, value) => setEmployeeGroup(value)}
                        options={employeeGroups}
                        disableClearable
                        autoSelect
                        disablePortal
                        disabled={searchIsOn}
                        value={employeeGroup}
                    />
                    <Button
                        style={{ marginLeft: "3rem" }}
                        id="save_button"
                        variant="save"
                        onClick={() => saveParameters()}
                        key="btn-save"
                        disabled={saveButtonDisabled}
                    >
                        {Translations.Save}
                    </Button>
                </Grid2>
            }
        >
            <>
                <LoadingIndicator loading={searchIsOn} />
                {!settings ? null : (
                    <Grid2 container>
                        <CalcShiftSettings
                            translate={translate}
                            handleChange={handleChange}
                            settings={settings}
                        />
                        <LunchBreak
                            translate={translate}
                            handleChange={handleChange}
                            settings={settings}
                            setSettings={setSettings}
                        />
                        <CalcDailyAdditions
                            translate={translate}
                            handleChange={handleChange}
                            settings={settings}
                        />
                        <CalcOtherAdditions
                            translate={translate}
                            handleChange={handleChange}
                            settings={settings}
                        />
                        <CalcOvertimes
                            translate={translate}
                            handleChange={handleChange}
                            settings={settings}
                            setSettings={setSettings}
                        />
                        <CalcDailyAllowance
                            translate={translate}
                            settings={settings}
                            setSettings={setSettings}
                        />
                        <Rounding
                            translate={translate}
                            handleChange={handleChange}
                            settings={settings}
                            setSettings={setSettings}
                        />
                        {ownerParameters.getOptionOwnerHasFillingHoursCalcSettingEnabled() && (
                            <CalcFillingHours
                                translate={translate}
                                handleChange={handleChange}
                                settings={settings}
                            />
                        )}
                    </Grid2>
                )}
            </>
        </MainLayout>
    );
};
